import { fmtMinutes } from '@/lib/geofence-rules';

import type { Alert } from '@logistech/ui-shared';

// Aligné sur l'enum AlertType (prisma/schema.prisma)
export const ALERT_TYPE_LABELS: Record<string, string> = {
  SPEEDING: 'Excès de vitesse',
  HARSH_BRAKING: 'Freinage brusque',
  HARSH_ACCELERATION: 'Accélération brusque',
  HARSH_CORNERING: 'Virage brusque',
  GEOFENCE_ENTRY: 'Entrée de zone',
  GEOFENCE_EXIT: 'Sortie de zone',
  GEOFENCE_DWELL: 'Stationnement prolongé',
  IDLE_PROLONGED: 'Ralenti prolongé',
  NIGHT_DRIVING: 'Conduite de nuit',
  ROUTE_DEVIATION: "Écart d'itinéraire",
  STOP_UNAUTHORIZED: 'Arrêt non autorisé',
  FUEL_DROP: 'Baisse de carburant',
  FUEL_THEFT_SUSPECTED: 'Vol de carburant suspecté',
  DEVICE_OFFLINE: 'Boîtier hors ligne',
  POWER_DISCONNECT: 'Alimentation coupée',
  IGNITION_OFF_UNEXPECTED: 'Coupure de contact inattendue',
  PANIC_BUTTON: 'Bouton SOS',
  IMPACT_DETECTED: 'Choc détecté',
  DOCUMENT_EXPIRING: 'Document à échéance',
  MAINTENANCE_DUE: 'Maintenance à prévoir',
  CUSTOM: 'Autre',
};

export const alertTypeLabel = (type: string): string => ALERT_TYPE_LABELS[type] ?? type;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * Titre et détail lisibles d'une alerte, reconstruits depuis le contexte posé par le rules-engine
 * (geofence-detector.ts : geofenceName, speedKmh / limit, dwellMin / threshold, dwellSec). Repli sur le
 * titre / la description stockés quand le contexte ne suffit pas (alertes anciennes, autres détecteurs).
 */
export const alertDisplay = (a: Pick<Alert, 'type' | 'title' | 'description' | 'context'>): { title: string; detail: string | null } => {
  const c = a.context ?? {};
  const zone = str(c['geofenceName']);
  switch (a.type) {
    case 'GEOFENCE_ENTRY':
      if (zone) return { title: `Entrée dans ${zone}`, detail: null };
      break;
    case 'GEOFENCE_EXIT': {
      if (!zone) break;
      const sec = num(c['dwellSec']);
      return { title: `Sortie de ${zone}`, detail: sec != null ? `Présence dans la zone : ${fmtMinutes(Math.max(1, Math.round(sec / 60)))}` : null };
    }
    case 'GEOFENCE_DWELL': {
      if (!zone) break;
      const dwell = num(c['dwellMin']);
      const threshold = num(c['threshold']);
      return {
        title: `Stationnement prolongé dans ${zone}`,
        detail: dwell != null ? `${fmtMinutes(dwell)} dans la zone${threshold != null ? ` (seuil : ${fmtMinutes(threshold)})` : ''}` : null,
      };
    }
    case 'SPEEDING': {
      const speed = num(c['speedKmh']);
      const limit = num(c['limit']);
      if (speed == null || limit == null) break;
      return {
        title: zone ? `Dépassement de vitesse dans ${zone}` : 'Dépassement de vitesse',
        detail: `${Math.round(speed)} km/h (limite : ${limit} km/h)`,
      };
    }
  }
  return { title: a.title, detail: a.description };
};
